import { FC } from 'react';
import { Stack } from '@mui/material';
import { Modal, TextField } from 'melp-design/components';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { useUpdateEmployeeGroup } from 'store/employee-groups';
import { ModalProps } from 'store/modal';
import { usePredefinedToasts } from 'utils/Toast';

interface Values {
  name: string;
}

interface Props extends ModalProps {
  employeeGroupId: string;
  initialValues: {
    name: string;
  };
}

export const UpdateEmployeeGroupModal: FC<Props> = ({
  employeeGroupId,
  initialValues,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { handleSubmit, control } = useForm<Values>({
    defaultValues: {
      name: initialValues.name,
    },
  });

  const { mutate: updateEmployeeGroup, isLoading } = useUpdateEmployeeGroup();

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateEmployeeGroup(
      { employeeGroupId, data: { name: values.name } },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          closeModal();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('groups.edit_group_name')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: isLoading,
          },
        ]}
      >
        <Stack gap="20px">
          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('groups.group_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
